import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class MatomoLazyLoaderService {
  private matomoInitialized = false;
  private matomoTracker: any;

  constructor(private router: Router) {}

  /**
   * Lazy loads and initializes Matomo tracking after user login
   * This reduces initial bundle size by ~100KB
   */
  async initializeMatomo(): Promise<void> {
    // Matomo tracking is currently disabled
    return;

    if (this.matomoInitialized) {
      return;
    }

    // Only initialize if Matomo is configured
    if (!environment.matomo?.url || !environment.matomo?.siteId) {
      return;
    }

    try {
      // Create the Matomo script dynamically
      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.async = true;
      script.defer = true;

      // Matomo tracking code
      const matomoCode = `
        var _paq = window._paq = window._paq || [];
        _paq.push(['trackPageView']);
        _paq.push(['enableLinkTracking']);
        (function() {
          var u="${environment.matomo.url}";
          _paq.push(['setTrackerUrl', u+'matomo.php']);
          _paq.push(['setSiteId', '${environment.matomo.siteId}']);
          var d=document, g=d.createElement('script'), s=d.getElementsByTagName('script')[0];
          g.type='text/javascript'; g.async=true; g.defer=true;
          g.src=u+'matomo.js';
          s.parentNode.insertBefore(g,s);
        })();
      `;

      script.innerHTML = matomoCode;
      document.head.appendChild(script);

      // Track router navigation
      this.setupRouterTracking();

      this.matomoInitialized = true;
    } catch (error) {
      console.error('Failed to initialize Matomo:', error);
      // Don't throw - Matomo is optional
    }
  }

  /**
   * Setup automatic page tracking on route changes
   */
  private setupRouterTracking(): void {
    this.router.events.subscribe((event: any) => {
      try {
        if (event.url && window._paq) {
          window._paq.push(['setCustomUrl', event.url]);
          window._paq.push(['trackPageView']);
        }
      } catch (error) {
        // Silently fail - tracking is non-critical
      }
    });
  }

  /**
   * Track a custom event
   */
  trackEvent(category: string, action: string, name?: string, value?: number): void {
    if (window._paq) {
      window._paq.push(['trackEvent', category, action, name, value]);
    }
  }

  /**
   * Track a button click
   */
  trackButtonClick(buttonName: string): void {
    this.trackEvent('UI', 'Button Click', buttonName);
  }

  isInitialized(): boolean {
    return this.matomoInitialized;
  }
}

// Declare global _paq variable for TypeScript
declare global {
  interface Window {
    _paq: any[];
  }
}